export type StressCopingStyle = 'auto' | 'conserve_energy' | 'movement_first'
export type StressInterventionMode = 'deload' | 'maintain'

export type StressEventContext = {
  eventTitle: string
  eventStart: string
  eventEnd: string
  hoursUntilEvent: number
  stressTier: 'high' | 'medium' | 'low'
  matchedKeywords: string[]
  expectedHrvDropPct: number | null
  isWorkEvent: boolean
  interventionMode: StressInterventionMode
  personalSampleCount: number
  personalHrvImpactPct: number | null
  learningApplied: boolean
  effectiveCopingStyle: StressCopingStyle
}

const STRESS_TIER_LABEL: Record<StressEventContext['stressTier'], string> = {
  high: 'High cognitive load',
  medium: 'Moderate cognitive load',
  low: 'Low cognitive load',
}

export function stressTierLabel(tier: StressEventContext['stressTier']): string {
  return STRESS_TIER_LABEL[tier] ?? STRESS_TIER_LABEL.medium
}

export function parseStressEventContext(trigger: Record<string, unknown>): StressEventContext | null {
  const eventTitle = String(trigger.event_title ?? '').trim()
  const eventStart = String(trigger.event_start ?? '').trim()
  if (!eventTitle || !eventStart) return null

  const startMs = new Date(eventStart).getTime()
  const fallbackEnd = new Date(startMs + 60 * 60 * 1000).toISOString()
  const eventEnd = String(trigger.event_end ?? '').trim() || fallbackEnd

  const tierRaw = String(trigger.stress_tier ?? 'medium')
  const stressTier: StressEventContext['stressTier'] =
    tierRaw === 'high' || tierRaw === 'low' ? tierRaw : 'medium'

  const interventionRaw = String(trigger.intervention_mode ?? 'deload')
  const interventionMode: StressInterventionMode =
    interventionRaw === 'maintain' ? 'maintain' : 'deload'

  const copingRaw = String(trigger.effective_coping_style ?? 'auto')
  const effectiveCopingStyle: StressCopingStyle =
    copingRaw === 'movement_first' || copingRaw === 'conserve_energy' ? copingRaw : 'auto'

  return {
    eventTitle,
    eventStart,
    eventEnd,
    hoursUntilEvent: Number(trigger.hours_until_event ?? 0),
    stressTier,
    matchedKeywords: Array.isArray(trigger.matched_keywords)
      ? trigger.matched_keywords.map((kw) => String(kw))
      : [],
    expectedHrvDropPct:
      trigger.expected_hrv_drop_pct != null ? Number(trigger.expected_hrv_drop_pct) : null,
    isWorkEvent: Boolean(trigger.is_work_event),
    interventionMode,
    personalSampleCount: Number(trigger.personal_sample_count ?? 0),
    personalHrvImpactPct:
      trigger.personal_hrv_impact_pct != null ? Number(trigger.personal_hrv_impact_pct) : null,
    learningApplied: Boolean(trigger.learning_applied),
    effectiveCopingStyle,
  }
}

export function formatEventCountdown(hoursUntilEvent: number): string {
  if (hoursUntilEvent < 1) return 'Starting within the hour'
  if (hoursUntilEvent < 24) {
    const rounded = Math.round(hoursUntilEvent)
    return `Starts in ${rounded} hour${rounded === 1 ? '' : 's'}`
  }
  const days = Math.floor(hoursUntilEvent / 24)
  const hours = Math.round(hoursUntilEvent % 24)
  if (hours === 0) return `Starts in ${days} day${days === 1 ? '' : 's'}`
  return `Starts in ${days}d ${hours}h`
}

export function stressEventCoachSummary(context: StressEventContext): string {
  const timing = formatEventCountdown(context.hoursUntilEvent).toLowerCase()
  const tier = stressTierLabel(context.stressTier).toLowerCase()

  if (context.interventionMode === 'maintain') {
    const learned =
      context.learningApplied && context.personalHrvImpactPct != null
        ? ` Your history after similar events averages ${context.personalHrvImpactPct >= 0 ? '+' : ''}${context.personalHrvImpactPct}% HRV — training before this works for you.`
        : context.effectiveCopingStyle === 'movement_first'
          ? ' You prefer using training as a stress buffer.'
          : ''
    return `"${context.eventTitle}" is ${tier} (${timing}). Kilo recommends keeping a moderate session — sub-max effort, no PRs.${learned}`
  }

  const learned =
    context.learningApplied && context.personalHrvImpactPct != null
      ? ` Similar events in your history averaged ${context.personalHrvImpactPct}% next-day HRV.`
      : ''
  return `"${context.eventTitle}" is ${tier} (${timing}). Training and nutrition are adjusted to protect energy before it.${learned}`
}

export function confidenceLearningHint(context: StressEventContext): string | null {
  if (context.personalSampleCount < 2) {
    return 'Confidence will rise as Kilo observes more events like this in your calendar + wearable data.'
  }
  return `Personalized from ${context.personalSampleCount} similar events in your history.`
}
